
import { useState, useEffect } from 'react';

const HeroSection: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const slides = [
    {
      title: "Premium OOH Advertising in GCC & Asia",
      subtitle: "Strategic billboard placements across Dubai, Riyadh, Seoul, and Tokyo's most valuable locations",
      image: "https://public.readdy.ai/ai/img_res/9683bdde89a61b08dba01e0cc14ed3af.jpg",
      cta: "Explore GCC Markets"
    },
    {
      title: "Times Square & Manhattan Billboard Network",
      subtitle: "Dominate New York's iconic advertising landscape with premium digital displays and strategic placements",
      image: "https://readdy.ai/api/search-image?query=Times%20Square%20New%20York%20City%20at%20night%20with%20massive%20digital%20billboards%20and%20LED%20screens%20displaying%20advertisements%2C%20bustling%20crowds%20of%20people%20walking%20through%20the%20iconic%20intersection%2C%20bright%20neon%20lights%20illuminating%20the%20urban%20landscape%2C%20professional%20advertising%20photography%20showcasing%20the%20scale%20and%20impact%20of%20outdoor%20advertising%20in%20Manhattan&width=1200&height=600&seq=times-square-billboards-2024&orientation=landscape",
      cta: "Get NYC Quote"
    },
    {
      title: "International Airport Premium Advertising",
      subtitle: "Capture global audiences with exclusive airport terminal displays and approach advertising",
      image: "https://readdy.ai/api/search-image?query=Modern%20international%20airport%20terminal%20with%20premium%20digital%20advertising%20displays%20and%20LED%20screens%2C%20sophisticated%20travelers%20walking%20through%20spacious%20corridors%20with%20luxury%20brand%20advertisements%2C%20contemporary%20airport%20architecture%20with%20high-end%20commercial%20advertising%20infrastructure%2C%20professional%20aviation%20photography%20with%20clean%20modern%20lighting&width=1200&height=600&seq=airport-premium-ads-2024&orientation=landscape",
      cta: "Airport Solutions"
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const handleGetStarted = () => {
    if (window.REACT_APP_NAVIGATE) {
      window.REACT_APP_NAVIGATE('/portal');
    }
  };

  const scrollToServices = () => {
    const servicesSection = document.getElementById('services');
    if (servicesSection) {
      servicesSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleCTAClick = () => {
    const currentCTA = slides[currentSlide].cta;
    
    if (currentCTA === "Explore GCC Markets") {
      const event = new CustomEvent('openContactModal', { 
        detail: { type: 'gcc-markets' } 
      });
      window.dispatchEvent(event);
    } else if (currentCTA === "Get NYC Quote") {
      const event = new CustomEvent('openContactModal', { 
        detail: { type: 'nyc-quote' } 
      });
      window.dispatchEvent(event);
    } else if (currentCTA === "Airport Solutions") {
      const event = new CustomEvent('openContactModal', { 
        detail: { type: 'airport-solutions' } 
      });
      window.dispatchEvent(event);
    }
  };

  return (
    <section id="hero-section" className="relative min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 overflow-hidden">
      {/* Background Images */}
      {slides.map((slide, index) => (
        <div
          key={index}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === currentSlide ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={slide.image}
            alt={slide.title}
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-black/60 sm:bg-black/50 md:bg-black/40"></div>
        </div>
      ))}

      {/* Content */}
      <div className="relative z-10 h-full flex items-center min-h-screen">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center lg:text-left max-w-4xl mx-auto lg:mx-0 pt-16 sm:pt-20 lg:pt-0">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-light text-white mb-4 sm:mb-6 lg:mb-8 leading-tight">
              {slides[currentSlide].title}
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-white/90 font-light mb-8 sm:mb-10 lg:mb-12 max-w-3xl mx-auto lg:mx-0 leading-relaxed">
              {slides[currentSlide].subtitle}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center lg:justify-start">
              <button 
                onClick={handleGetStarted}
                className="bg-blue-600 text-white px-8 sm:px-10 py-4 sm:py-5 rounded-lg hover:bg-blue-700 transition-colors font-medium text-base sm:text-lg lg:text-xl whitespace-nowrap cursor-pointer"
              >
                Get Campaign Quote
              </button>
              <button 
                onClick={handleCTAClick}
                className="border-2 border-white text-white px-8 sm:px-10 py-4 sm:py-5 rounded-lg hover:bg-white hover:text-gray-900 transition-colors font-medium text-base sm:text-lg lg:text-xl whitespace-nowrap cursor-pointer"
              >
                {slides[currentSlide].cta}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Slide Indicators */}
      <div className="absolute bottom-6 sm:bottom-8 lg:bottom-12 left-1/2 transform -translate-x-1/2 z-20">
        <div className="flex space-x-3 sm:space-x-4">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 sm:w-4 sm:h-4 rounded-full transition-colors cursor-pointer ${
                index === currentSlide ? 'bg-white' : 'bg-white/50'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Navigation Arrows - Hidden on mobile */}
      <button
        onClick={() => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length)}
        className="hidden sm:flex absolute left-4 lg:left-8 top-1/2 transform -translate-y-1/2 z-20 w-12 h-12 lg:w-16 lg:h-16 bg-white/20 hover:bg-white/30 rounded-full items-center justify-center transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-line text-white text-xl lg:text-2xl"></i>
      </button>
      
      <button
        onClick={() => setCurrentSlide((prev) => (prev + 1) % slides.length)}
        className="hidden sm:flex absolute right-4 lg:right-8 top-1/2 transform -translate-y-1/2 z-20 w-12 h-12 lg:w-16 lg:h-16 bg-white/20 hover:bg-white/30 rounded-full items-center justify-center transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-line text-white text-xl lg:text-2xl"></i>
      </button>

      {/* Mobile Swipe Indicators */}
      <div className="sm:hidden absolute bottom-20 left-1/2 transform -translate-x-1/2 z-20">
        <div className="text-white/70 text-sm flex items-center gap-2">
          <i className="ri-arrow-left-right-line"></i>
          <span>Swipe to explore</span>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
